import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CampaignClient,
  DebitOrderMandateClient,
  CampaignDTO,
  DebitOrderMandateDTO,
  DonationDTO,
  MemberClient,
  MemberDTO,
  PaymentClient,
  QuestionSetItemAnswerDTO,
  QuestionSetItemClient,
  QuestionSetItemDTO,
  SearchRequestDTO_questionSetItemSearchRequestDTO,
} from '../../../services/apiClient';
import { API_BASE_URL } from '../../../config/config';
import axiosInstance from '../../../services/axiosInstance';
import Stepper from '../../../components/common/Stepper';
import DonationOptions from '../../../components/campaign-donation/DonationOptions';
import CaptureAccountDetails from '../../../components/campaign-donation/CaptureAccountDetails';
import CaptureUserDetails from '../../../components/campaign-donation/CaptureUserDetails';
import useRewards from '../../../hooks/useRewards';
import usePaymentStatus from '../../../hooks/usePaymentStatus';

const CampaignDonation = () => {
  const { campaignId } = useParams();
  const [_campaignId, setCampaignId] = useState<string>('');

  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<CampaignDTO>({});
  const [questionSetItems, setQuestionSetItems] = useState<
    QuestionSetItemDTO[]
  >([]);
  const [steps, setSteps] = useState(['Step 1', 'Step 2', 'Finish']);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [debitOrderMandate, setDebitOrderMandate] =
    useState<DebitOrderMandateDTO>({});
  const [donation, setDonation] = useState<DonationDTO | null>({
    amount: 150,
    frequency: 'monthly',
    dayOfMonth: 1,
  } as DonationDTO);
  const [member, setMember] = useState<MemberDTO | null>(null);
  const [answers, setAnswers] = useState<QuestionSetItemAnswerDTO[]>([]);
  const { paymentStatuses } = usePaymentStatus();
  const campaignClient = new CampaignClient(API_BASE_URL, axiosInstance);
  const debitOrderMandateClient = new DebitOrderMandateClient(
    API_BASE_URL,
    axiosInstance,
  );
  const memberClient = new MemberClient(API_BASE_URL, axiosInstance);
  const paymentClient = new PaymentClient(API_BASE_URL, axiosInstance);
  const questionSetItemClient = new QuestionSetItemClient(
    API_BASE_URL,
    axiosInstance,
  );
  const navigate = useNavigate();

  ////////////////////////////////////
  /// FUNCTIONS
  ////////////////////////////////////

  const getImageSas = async () => {
    try {
      if (_campaignId) {
        const response =
          await campaignClient.generateCampaignImageSas(_campaignId);
        if (response && response.success) {
          setBackgroundImage(response.data ?? '');
        }
      }
    } catch (error) {
      console.error('Error fetching campaign image:', error);
    }
  };

  const getCampaign = async () => {
    try {
      if (_campaignId) {
        const response = await campaignClient.getCampaignById(_campaignId);
        if (response && response.success) {
          setCampaign(response.data ?? {});
        }
      }
    } catch (error) {
      console.error('Error fetching campaign image:', error);
    }
  };

  const fetchMemberByEmail = async (email: string) => {
    try {
      const result = await memberClient.getMemberByEmail(email);
      if (result && result.success && result.data) {
        setMember(result.data);
      }
    } catch (err) {
      console.error('Failed to fetch member:', err);
    }
  };

  const fetchQuestions = async () => {
    const searchRequest: SearchRequestDTO_questionSetItemSearchRequestDTO = {
      filters: {
        questionSetId: campaign.questionSetId || '',
      },
      page: 1,
      pageSize: 100,
      sortBy: 'OrderSequence',
      sortDescending: false,
    };

    try {
      const response =
        await questionSetItemClient.searchQuestionSetItems(searchRequest);
      if (response && response.data) {
        setQuestionSetItems(
          (response.data?.results as QuestionSetItemDTO[]) ?? [],
        );
      }
    } catch (error) {
      console.error(`Error fetching question set items:`, error);
    } finally {
    }
  };

  ////////////////////////////////////
  /// EVENTS
  ////////////////////////////////////

  const handleDebitOrderMandateChange = (
    updated: Partial<DebitOrderMandateDTO>,
  ) => {
    setDebitOrderMandate(
      (prev) => ({ ...prev, ...updated }) as DebitOrderMandateDTO,
    );
  };

  const handleDonationChange = (updated: Partial<DonationDTO>) => {
    setDonation((prev) => ({ ...prev, ...updated }) as DonationDTO);
  };

  const handleMemberChange = (updated: Partial<MemberDTO>) => {
    setMember((prev) => ({ ...prev, ...updated }) as MemberDTO);
  };

  const handleDonationSubmit = async () => {
    if (!member?.id || !_campaignId || !donation) {
      console.error('Missing member, donation campaign ID');
      return;
    }

    const pendingStatus = paymentStatuses.find(
      (p) => p.name?.toLowerCase() === 'pending',
    );

    if (!pendingStatus) {
      console.error('Pending payment status not found');
      return;
    }

    setIsSubmitting(true);
    try {
      //WHEN THE PERSON SELECTED MONTHLY WE SOULD NOT CREATE PAYMENT BUT WE CAN SUBMIT THE DETAILS TO THE NETCASH SERVICE TO UPLOAD TO THE MASTER FILE
      if (donation.frequency !== 'monthly') {
        var response = await paymentClient.createPayment({
          memberId: member.id,
          campaignId: _campaignId,
          amount: donation.amount,
          paymentStatusId: pendingStatus.id,
          subscription: false,
          debitDay: undefined,
        });

        if (response && response.success && response.data) {
          navigate(`/campaign-donation/redirect/${response.data.id}`);
        }
      } else {
        const response = await debitOrderMandateClient.uploadDebitOrderMandate(
          _campaignId,
          debitOrderMandate,
        );
        if (response && response.success && response.data) {
          navigate(`/campaign-donation-debit-order/redirect/${_campaignId}`);
        }
      }
    } catch (error) {
      console.error('Failed to create payment', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  ////////////////////////////////////
  /// HOOKS
  ////////////////////////////////////

  useEffect(() => {
    if (campaignId) {
      const idToSet =
        campaignId === '4'
          ? '9e9d1fcf-3a4e-46a7-b873-ebb8cfff77cd'
          : campaignId;
      setCampaignId(idToSet);
    }
  }, [campaignId]);

  useEffect(() => {
    if (_campaignId) {
      getImageSas();
      getCampaign();
    }
  }, [_campaignId]);

  useEffect(() => {
    if (campaign.questionSetId) {
      fetchQuestions();
    }
  }, [campaign?.questionSetId]);

  useEffect(() => {
    if (campaign.debitOnly && donation?.frequency !== 'monthly') {
      setDonation((prev) => ({ ...prev, frequency: 'monthly', amount: 150 }) as DonationDTO);
    }
  }, [campaign?.debitOnly]);

  useEffect(() => {
    if (donation && donation !== debitOrderMandate?.donation) {
      setDebitOrderMandate((prev) => ({ ...prev, donation }));
    }
  }, [donation]);

  useEffect(() => {
    if (donation?.frequency === 'once') {
      setSteps(['Step 1', 'Finish']);
    } else if (donation?.frequency === 'monthly') {
      setSteps(['Step 1', 'Step 2', 'Finish']);
    }
  }, [donation?.frequency]);

  useEffect(() => {
    if (member?.email && member.email !== debitOrderMandate?.email) {
      //NOW WE LOOK  FOR THE MEBER BY EMAIL
      fetchMemberByEmail(member?.email);

      //NOW WE SYNC EMAIL TO MANDATE
      setDebitOrderMandate(
        (prev) => ({ ...prev, email: member?.email }) as DebitOrderMandateDTO,
      );
    }
  }, [member?.email]);

  // Add new useEffect to sync member ID
  useEffect(() => {
    if (member?.id && member.id !== debitOrderMandate?.memberId) {
      setDebitOrderMandate(
        (prev) =>
          ({
            ...prev,
            memberId: member.id,
          }) as DebitOrderMandateDTO,
      );
    }
  }, [member?.id]);

  ////////////////////////////////////
  /// RENDERS & HTML
  ////////////////////////////////////

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <DonationOptions
            donation={donation ?? {}}
            onChange={handleDonationChange}
            onNext={() =>
              setCurrentStep(donation?.frequency === 'monthly' ? 2 : 3)
            }
            debitOnly={campaign.debitOnly}
          />
        );
      case 2:
        return (
          <CaptureAccountDetails
            debitOrderMandate={debitOrderMandate ?? {}}
            donation={donation ?? {}}
            member={member ?? {}}
            onDebitOrderMandateChange={handleDebitOrderMandateChange}
            onMemberChange={handleMemberChange}
            onBack={() => setCurrentStep(1)}
            onNext={() => setCurrentStep(3)}
          />
        );
      case 3:
        return (
          <CaptureUserDetails
            campaignId={_campaignId ?? ''}
            debitOrderMandate={debitOrderMandate ?? {}}
            donation={donation ?? {}}
            member={member ?? {}}
            answers={answers}
            setAnswers={setAnswers}
            questionSetItems={questionSetItems ?? []}
            onChange={handleMemberChange}
            onDebitOrderMandateChange={handleDebitOrderMandateChange}
            onBack={() =>
              setCurrentStep(donation?.frequency === 'monthly' ? 2 : 1)
            }
            onSubmit={handleDonationSubmit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-12">
      {backgroundImage && (
        <div
          className="absolute inset-0 w-full min-h-full bg-cover bg-center transition-opacity duration-500 -z-10"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
      )}

      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg text-center z-10">
        <img
          src="/images/logo.png"
          alt="Campaign Logo"
          className="mx-auto mb-6 w-56"
        />

        <Stepper currentStep={currentStep} steps={steps} />

        <div className="mt-5 sm:mt-8">{renderStepContent()}</div>
      </div>
    </div>
  );
};

export default CampaignDonation;
